import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface CaptureContextValue {
  file: File | null;
  templateType: 'meeting' | 'task' | 'medical';
  setTemplateType: (value: 'meeting' | 'task' | 'medical') => void;
  setCapture: (file: File | null) => void;
  clearCapture: () => void;
}

const CaptureContext = createContext<CaptureContextValue | null>(null);

export function CaptureProvider({ children }: { children: ReactNode }) {
  const [file, setFile] = useState<File | null>(null);
  const [templateType, setTemplateType] = useState<'meeting' | 'task' | 'medical'>('medical');
  const setCapture = useCallback((next: File | null) => setFile(next), []);
  const clearCapture = useCallback(() => setFile(null), []);
  const value = useMemo(
    () => ({ file, templateType, setTemplateType, setCapture, clearCapture }),
    [file, templateType, setCapture, clearCapture],
  );
  return (
    <CaptureContext.Provider value={value}>
      {children}
    </CaptureContext.Provider>
  );
}

export function useCapture() {
  const context = useContext(CaptureContext);
  if (!context) throw new Error('useCapture must be used inside CaptureProvider');
  return context;
}
